import React, { useCallback, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useQuery } from "@apollo/client/react";
import { GET_CLIENT_WORKERS_AND_INVITATION_WORKERS } from "../../graphql/client";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { ListGroupItem, useListGroupRenderItem } from "../../components/ListGroup";
import { InvitationTarget, WorkerInfo } from "../../__generated__/graphql";
import {
    InvitationFieldsFragment,
    WorkerInfoFieldsFragment,
} from "../../__generated__/operations/graphql";
import { StackScreenProps } from "@react-navigation/stack";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet } from "react-native";
import Button from "../../components/Button";
import EmptyScreen from "../../components/EmptyScreen";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import NoTalentsImage from "../../assets/images/no-talents-image.svg";
import { useTracking } from "../../hooks/tracking";
import ScreenCard from "../ScreenCard";
import { useBusinessEmailVerificationHeadband } from "../../hooks/use-business-email-verification-headband";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import { useRelevantMissionCounter } from "../../hooks/missions";
import Loading from "../../components/Loading";

type RootParams = StackScreenProps<RootStackParamList>;
type ScreenParams = StackScreenProps<ClientWorkersTabParamList>;

const ClientWorkersScreen: React.FC<ScreenParams> = ({ navigation }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const emailVerificationHeadband = useBusinessEmailVerificationHeadband();
    const { loading, error, data, refetch } = useQuery(GET_CLIENT_WORKERS_AND_INVITATION_WORKERS, {
        fetchPolicy: "cache-and-network",
    });
    const { trackEvent } = useTracking();
    const workers = useMemo(() => data?.getClientWorkers || [], [data?.getClientWorkers]);
    const invitations = useMemo(
        () => data?.workerInvitationsSent || [],
        [data?.workerInvitationsSent],
    );
    const getMissionCount = useRelevantMissionCounter();
    const items = useMemo<ListGroupItem<string>[]>(() => {
        const unionArray: (WorkerInfoFieldsFragment | InvitationFieldsFragment)[] = [
            ...workers,
            ...invitations,
        ];
        const navigateToItem = (item: WorkerInfoFieldsFragment | InvitationFieldsFragment) => {
            if ("mode" in item) {
                // Invitation
                navigation.push("SendInvitation", {
                    invitationId: item.id,
                    target: InvitationTarget.Worker,
                });
                return;
            }
            navigation.push("WorkerDetails", { workerId: item.id });
        };
        const nonSortedItems = unionArray.map((worker): ListGroupItem<string> => {
            const isInvitation = "mode" in worker;
            // An invitation sent by email (from the web app) has no phone number.
            let workerName = worker.phoneNumber || (isInvitation && worker.email) || "";
            if (isInvitation) {
                if (worker.firstName) {
                    workerName = `${worker.firstName} `;
                }
                if (worker.lastName) {
                    workerName += worker.lastName;
                }
            } else {
                const workerInfo = worker as WorkerInfo;
                if (workerInfo.name) {
                    workerName = workerInfo.name;
                }
            }
            const workerInCreation = !isInvitation && !(worker as WorkerInfo).name;
            return {
                value: worker.id,
                title: workerName,
                icon: isInvitation ? "talent" : undefined,
                iconColor: isInvitation ? colors.onSurfaceDisabled : undefined,
                avatarPath: isInvitation ? undefined : (worker as WorkerInfo).avatarUrl,
                useBigAvatarOrIcon: true,
                description:
                    isInvitation || workerInCreation
                        ? undefined
                        : i18n.t("client_worker_cell.mission_count", {
                              count: getMissionCount((worker as WorkerInfo).missionCount),
                          }),
                labels: [
                    {
                        text: isInvitation ? i18n.t("client_worker_cell.invitation") : undefined,
                        mode: "warning" as const,
                    },
                    {
                        text: workerInCreation
                            ? i18n.t("client_worker_cell.incomplete_profile")
                            : undefined,
                        mode: "warning" as const,
                    },
                ],
                onPress: workerInCreation
                    ? undefined
                    : () => {
                          navigateToItem(worker);
                      },
            };
        });
        return nonSortedItems.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
    }, [workers, invitations, navigation, colors, i18n, getMissionCount]);

    const goToSendInvitation = useCallback(() => {
        void trackEvent("click_create_invitation");
        (navigation as RootParams["navigation"]).navigate("SendInvitation", {
            target: InvitationTarget.Worker,
        });
    }, [navigation, trackEvent]);

    const dynamicButtonProps = useDynamicHeaderButton(
        items.length > 0 && {
            title: i18n.t("screens.client_profile.tabs.employees.invite_talent"),
            shortTitle: i18n.t("screens.client_profile.tabs.employees.employee"),
            onClick: goToSendInvitation,
        },
    );

    const isInitialLoading = loading && !data;

    const renderRow = useListGroupRenderItem(items, { isItemsCheckable: false });

    // The populated list is virtualized by ScreenCard; loading/empty/error states keep the
    // default ScrollView path (the virtualized list cannot render those non-list layouts).
    const showList = !error && !isInitialLoading && items.length > 0;

    return (
        <ScreenCard
            refresh={refetch}
            headband={emailVerificationHeadband}
            isContentContainerFlex={!error && (items.length === 0 || isInitialLoading)}
            {...(!error ? dynamicButtonProps : {})}
            virtualizedItems={showList ? items : undefined}
            renderItem={showList ? renderRow : undefined}
        >
            {showList ? undefined : (
                <LoadingBoundary
                    error={
                        error
                            ? i18n.t("screens.client_profile.tabs.employees.cannot_load_workers")
                            : undefined
                    }
                    loading={loading && !data}
                >
                    {isInitialLoading ? (
                        <Loading />
                    ) : items.length === 0 ? (
                        <>
                            <EmptyScreen
                                image={NoTalentsImage}
                                text={i18n.t("screens.client_profile.tabs.employees.no_workers")}
                            />
                            <Button
                                size="large"
                                testID="button-new-invitation"
                                style={styles.startButton}
                                onPress={goToSendInvitation}
                                label={i18n.t(
                                    "screens.client_profile.tabs.employees.invite_talent",
                                )}
                            />
                        </>
                    ) : null}
                </LoadingBoundary>
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                startButton: {
                    marginHorizontal: gridSize,
                    alignSelf: "stretch",
                },
            }),
        [gridSize],
    );
};

export default ClientWorkersScreen;
